import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Bell, Package, DollarSign, Upload, MapPin, Clock, ExternalLink, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const NotificationBox = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/traffic/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.stats?.unread || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId, event = null) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/traffic/notifications/mark-read/${notificationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setNotifications(notifications.map(n => 
          n._id === notificationId ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification, event) => {
    if (event.target.closest('.mark-read-btn')) {
      return;
    }
    
    if (!notification.is_read) {
      markAsRead(notification._id);
    }
    
    if (notification.redirect_url) {
      navigate(notification.redirect_url);
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return <Package className="h-4 w-4 text-blue-500" />;
      case 'payment':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      case 'payment_upload':
        return <Upload className="h-4 w-4 text-purple-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  // Format time ago
  const timeAgo = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Poll for new notifications every 5 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="h-full">
      <CardHeader className="border-b pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-emerald-600" />
            <CardTitle className="text-lg">Live Notifications</CardTitle>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                {unreadCount}
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-1">Real-time customer activity</p>
      </CardHeader>

      <CardContent className="p-0 max-h-[450px] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Bell className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="font-medium text-sm">No notifications yet</p>
            <p className="text-xs mt-1">Customer activities will appear here</p>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((notification, index) => (
              <div
                key={notification._id || index}
                onClick={(e) => handleNotificationClick(notification, e)}
                className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors relative h-[90px] flex items-center ${
                  !notification.is_read ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex gap-2 w-full">
                  {/* Icon */}
                  <div className="flex-shrink-0 pt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    {/* Top Section: Name, Action, Mark Button */}
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">
                            {notification.customer_name}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">
                            {notification.action}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <button
                            onClick={(e) => markAsRead(notification._id, e)}
                            className="mark-read-btn text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5 flex-shrink-0"
                            title="Mark as read"
                          >
                            <CheckCircle className="h-3 w-3" />
                          </button>
                        )}
                      </div>

                      {/* Location - Compact */}
                      {(notification.city || notification.state) && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500 truncate">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">
                            {notification.city}{notification.city && notification.state ? ', ' : ''}{notification.state}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Bottom Section: Amount/Order & Time */}
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-xs truncate flex-1">
                        {notification.details?.amount && (
                          <span className="text-green-700 font-semibold">₹{notification.details.amount}</span>
                        )}
                        {notification.details?.order_id && (
                          <span className="text-gray-500 ml-2 truncate">{notification.details.order_id}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="h-3 w-3" />
                          <span>{timeAgo(notification.created_at)}</span>
                        </div>
                        {notification.redirect_url && (
                          <ExternalLink className="h-3 w-3 text-blue-600" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationBox;
